import { useState } from 'react';
import { toast } from 'sonner';
import { RefreshCwIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { btnGhost } from '../ui/styles';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchIntegrations, refreshIntegration } from '../adminApi';
import { integrationInfo } from '../../data/insightIntegrations';
import { errorMessage } from '../../core/errors';
import { dateOf, dayShort, timeOf } from '../../core/format/dates';
import type { IntegrationId, IntegrationStatus } from '../../core/types/insights';
const order: IntegrationId[] = ['umami', 'searchConsole', 'googleBusiness', 'instagram', 'brevo'];
const badge = (s: IntegrationStatus) => s.status === 'gekoppeld' ? {
  tone: 'success' as const,
  label: 'Gekoppeld'
} : s.status === 'fout' ? {
  tone: 'soft' as const,
  label: 'Ophalen mislukt'
} : {
  tone: 'neutral' as const,
  label: 'Niet gekoppeld'
};

/**
 * Instellingen › Koppelingen & privacy › Koppelingen: per externe bron de status, wat hij oplevert en hoe je hem koppelt.
 * Koppelen zelf gebeurt op de server; hier staan nooit sleutels of wachtwoorden (docs/INZICHT.md §7).
 */
export function IntegrationSettings() {
  const {
    data,
    setData,
    status,
    reload
  } = useLatestLoad(fetchIntegrations);
  const [busy, setBusy] = useState<IntegrationId | null>(null);
  const refresh = async (id: IntegrationId) => {
    setBusy(id);
    try {
      const next = await refreshIntegration(id);
      setData(d => d ? {
        ...d,
        [id]: next
      } : d);
      toast.success(`${integrationInfo[id].name}: ophalen staat klaar`);
    } catch (err) {
      toast.error('Opnieuw ophalen lukte niet', {
        description: errorMessage(err)
      });
    } finally {
      setBusy(null);
    }
  };
  return <Panel title="Koppelingen" description="Waar Inzicht zijn cijfers vandaan haalt. De server haalt elke nacht op; koppelen gebeurt door wie de server beheert, met de stappen hieronder." flush data-id="element-4250" data-mp="2x5gfve34rnmg">
      {!data ? status === 'error' ? <LoadFailed variant="inline" title="De status van de koppelingen kon niet laden" onRetry={reload} className="px-4 py-6 sm:px-5" data-id="element-4251" data-mp="27ivs5yfx9c2u" /> : <LoadingLine className="px-4 py-6 sm:px-5" data-id="element-4252" data-mp="2mev8txh3kkjl" /> : <ul className="divide-y divide-line" data-id="element-4253" data-mp="2hhv35no5tjt0">
          {order.map(id => {
        const s = data[id];
        const info = integrationInfo[id];
        const b = badge(s);
        return <li key={id} className="px-4 py-4 sm:px-5" data-id="element-4254" data-mp="1otfpp5kof9js">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5" data-id="element-4255" data-mp="23hakqsm5pq01">
                  <span className="text-[14px] text-ink" data-id="element-4256" data-mp="1yq2bpxoqo6ne">{info.name}</span>
                  <Badge tone={b.tone} data-id="element-4257" data-mp="1ynza0ge84p2u">{b.label}</Badge>
                  {s.status !== 'niet-gekoppeld' && <button type="button" onClick={() => void refresh(id)} disabled={busy !== null} className={`${btnGhost} ml-auto disabled:opacity-50`} data-id="element-4258" data-mp="1ay4xy11qb4jn">
                      <RefreshCwIcon className={`h-3.5 w-3.5 ${busy === id ? 'animate-spin' : ''}`} strokeWidth={1.75} aria-hidden="true" data-id="element-4259" data-mp="3hnyiz9wnehcj" />
                      {busy === id ? 'Ophalen…' : 'Opnieuw ophalen'}
                    </button>}
                </div>
                <p className="mt-1 text-[13px] text-muted" data-id="element-4260" data-mp="3cp3ze5quword">{info.what}</p>
                {s.updatedAt && <p className="mt-1 text-[12px] text-muted" data-id="element-4261" data-mp="49k9ngpe9oh2">
                    Bijgewerkt tot {dayShort(dateOf(s.updatedAt))}, {timeOf(s.updatedAt)}
                  </p>}
                {s.message && <p className={`mt-1 text-[12px] ${s.status === 'fout' ? 'text-clay' : 'text-muted'}`} data-id="element-4262" data-mp="3rdi4knbqwpb3">{s.message}</p>}
                {s.status !== 'gekoppeld' && <ol className="mt-3 list-decimal space-y-1 pl-5 text-[13px] text-ink" data-id="element-4263" data-mp="2fzmo9b93uaia">
                    {info.steps.map(step => <li key={step} data-id="element-4264" data-mp="2k1rdlxsiskz4">{step}</li>)}
                  </ol>}
              </li>;
      })}
        </ul>}
    </Panel>;
}